import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, CardHeader, DatePicker, ErrorState, Input, PageHeader, PageLoader, Select, Textarea, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { LineItemsEditor, lineError, newLine, toApiLine, toEditLine, EditLine } from '../../../components/common/LineItemsEditor/LineItemsEditor';
import { CustomerPicker } from '../../../components/common/Pickers/Pickers';
import { FormSection, TableSection, TotalsBox, TwoColumn } from '../../../components/common/Parts/Parts';
import { useFetch } from '../../../hooks/useFetch';
import { withHomeState } from '../../../constants/location';
import { serverFieldErrors, trimOrUndef } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { toISODate } from '../../../utils/date';
import { computeTotals } from '../../../lib/pricing';
import { loadSettings } from '../../settings/services/settingsService';
import { invoiceService } from '../services/invoiceService';
import { customerService } from '../../customers/services/customerService';
import type { InvoiceInput, InvoiceType } from '../types/invoiceTypes';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';

const TYPES = [
  { value: 'gst', label: 'GST invoice', description: 'Tax invoice with CGST + SGST' },
  { value: 'non_gst', label: 'Non-GST invoice', description: 'Bill of supply, no tax' },
];
const isoOf = (d?: string | null) => (d ? toISODate(new Date(d)) : '');

const EMPTY = {
  invoiceType: 'gst' as InvoiceType, customerId: null as string | null, customerName: '', date: toISODate(new Date()), dueDate: '',
  siteLine: '', siteCity: '', sitePincode: '', customerSupplied: '', notes: '', terms: '',
};

export default function InvoiceForm() {
  const { id = '' } = useParams();
  const editing = !!id;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { form, setForm, set, errors, setErrors } = useForm(EMPTY);
  const [items, setItems] = useState<EditLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [hasGstin, setHasGstin] = useState(true);

  const { data: inv, loading, error, reload } = useFetch(
    () => (editing ? invoiceService.get(id) : Promise.resolve(null)),
    [id],
  );
  useCrumb(`/invoices/${id}`, inv ? inv.invoiceNo ?? 'Draft' : undefined);

  useEffect(() => {
    loadSettings()
      .then((s) => {
        setHasGstin(!!s.gstin);
        if (!editing) setForm((f) => ({ ...f, invoiceType: s.gstin ? 'gst' : 'non_gst', terms: f.terms || s.invoiceTerms || '' }));
      })
      .catch(() => {});
    if (!editing) {
      setItems([newLine()]);
      const cid = params.get('customerId');
      if (cid) customerService.get(cid).then((c) => pickCustomer(cid, c)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  useEffect(() => {
    if (!inv) return;
    setForm({
      invoiceType: inv.invoiceType, customerId: inv.customerId, customerName: inv.customer?.name ?? '', date: isoOf(inv.date), dueDate: isoOf(inv.dueDate),
      siteLine: inv.siteAddress?.line ?? '', siteCity: inv.siteAddress?.city ?? '', sitePincode: inv.siteAddress?.pincode ?? '',
      customerSupplied: (inv.customerSuppliedMaterials ?? []).join('\n'), notes: inv.notes ?? '', terms: inv.terms ?? '',
    });
    setItems(inv.items.map((l) => toEditLine(l, 'description')));
  }, [inv, setForm]);

  function pickCustomer(cid, c) {
    const a = c?.siteAddresses?.[0] ?? c?.billingAddress ?? {};
    setForm((f) => ({ ...f, customerId: cid, customerName: c?.name ?? '', siteLine: f.siteLine || a.line || '', siteCity: f.siteCity || a.city || '', sitePincode: f.sitePincode || a.pincode || '' }));
    setErrors((e) => ({ ...e, customerId: undefined }));
  }

  const isGst = form.invoiceType === 'gst';
  const totals = computeTotals(items.map((l) => ({ ...l, qty: Number(l.qty) })), isGst);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!form.customerId) errs.customerId = 'Pick the customer';
    if (!items.length) errs.lines = 'Add at least one line';
    else errs.lines = lineError(items) ?? undefined;
    if (isGst && !hasGstin) errs.invoiceType = 'Add your GSTIN in Settings first, or make a non-GST invoice';
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error(errs.lines || errs.invoiceType || 'Please fix the highlighted fields');

    const body: InvoiceInput = {
      invoiceType: form.invoiceType,
      customerId: form.customerId as string,
      date: form.date || undefined,
      dueDate: form.dueDate || undefined,
      siteAddress: withHomeState({ line: form.siteLine.trim(), city: form.siteCity.trim(), pincode: form.sitePincode.trim() }),
      items: items.map((l) => toApiLine(l, 'description')),
      customerSuppliedMaterials: form.customerSupplied.split('\n').map((s) => s.trim()).filter(Boolean),
      notes: trimOrUndef(form.notes),
      terms: trimOrUndef(form.terms),
    };
    setSaving(true);
    try {
      const data = id ? await invoiceService.update(id, body) : await invoiceService.create(body);
      toast.success(editing ? 'Draft updated' : 'Draft invoice saved');
      navigate(`/invoices/${data._id}`, { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (editing && loading && !inv) return <PageLoader />;
  if (inv && inv.status !== 'draft') return <ErrorState error={{ message: 'Only draft invoices can be edited. Cancel and re-issue instead.' }} />;

  const actions = (
    <>
      <Button variant="outline" onClick={() => navigate(editing ? `/invoices/${id}` : '/invoices')} disabled={saving}>Cancel</Button>
      <Button type="submit" form="invoice-form" leftIcon="check" loading={saving}>{editing ? 'Save draft' : 'Save as draft'}</Button>
    </>
  );

  return (
    <>
      <PageHeader
        title={editing ? 'Edit draft invoice' : 'New invoice'}
        subtitle={editing ? inv?.customer?.name : 'Saved as a draft. It gets its number when you finalize it.'}
        actions={actions}
      />
      <form id="invoice-form" onSubmit={submit} noValidate>
        <TwoColumn
          main={
            <>
              <FormSection title="Bill to">
                <CustomerPicker required value={form.customerId} selectedLabel={form.customerName} onChange={pickCustomer} error={errors.customerId} containerClassName="sm:col-span-2" />
                <Select label="Invoice type" options={TYPES} value={form.invoiceType} onChange={set('invoiceType')} error={errors.invoiceType} searchable={false} />
                <div />
                <DatePicker label="Invoice date" value={form.date} onChange={set('date')} clearable={false} />
                <DatePicker label="Due date" value={form.dueDate} onChange={set('dueDate')} min={form.date || undefined} hint="Leave empty to use the default from Settings" />
                <Textarea label="Site address" rows={2} value={form.siteLine} onChange={set('siteLine')} containerClassName="sm:col-span-2" />
                <Input label="City" value={form.siteCity} onChange={set('siteCity')} />
                <Input label="Pincode" inputMode="numeric" maxLength={6} value={form.sitePincode} onChange={set('sitePincode')} />
              </FormSection>

              <TableSection title="Items" subtitle="Labour and material being billed">
                <LineItemsEditor lines={items} onChange={setItems} isGst={isGst} textLabel="Description" addLabel="Add line" />
              </TableSection>

              <FormSection title="Notes">
                <Textarea
                  label="Material supplied by customer"
                  rows={2}
                  value={form.customerSupplied}
                  onChange={set('customerSupplied')}
                  containerClassName="sm:col-span-2"
                  hint="One per line. Printed as a note, never charged."
                />
                <Textarea label="Terms" rows={3} value={form.terms} onChange={set('terms')} maxLength={3000} containerClassName="sm:col-span-2" />
                <Textarea label="Notes" rows={2} value={form.notes} onChange={set('notes')} maxLength={2000} containerClassName="sm:col-span-2" />
              </FormSection>
              <div className="flex justify-end gap-2">{actions}</div>
            </>
          }
          aside={
            <Card>
              <CardHeader title="Summary" />
              <div className="p-5 pt-4">
                <TotalsBox totals={totals} isGst={isGst} />
              </div>
            </Card>
          }
        />
      </form>
    </>
  );
}
